import type { DocNode } from '@atlaskit/adf-schema/doc';

export type AdfSmartLink = {
	url: string;
	occurrenceIndex: number;
	count: number;
};

const CARD_NODE_TYPES = new Set(['inlineCard', 'blockCard', 'embedCard']);

type AdfNode = {
	type: string;
	content?: AdfNode[];
	attrs?: { url?: unknown };
	marks?: { type?: string; attrs?: { href?: unknown } }[];
};

/**
 * Walks the ADF tree depth-first and returns one entry per unique external address
 * in document order, with a count of how often each appears.
 */
export function extractSmartLinksFromAdf(
	doc: DocNode | null | undefined,
	includeLinkMarks = false,
): AdfSmartLink[] {
	if (!doc) {
		return [];
	}

	const countByUrl = new Map<string, number>();

	const addUrl = (url: unknown): void => {
		if (typeof url === 'string' && url) {
			countByUrl.set(url, (countByUrl.get(url) ?? 0) + 1);
		}
	};

	function walk(node: AdfNode): void {
		if (CARD_NODE_TYPES.has(node.type)) {
			addUrl(node.attrs?.url);
		}

		if (includeLinkMarks) {
			addUrl(node.marks?.find((mark) => mark?.type === 'link')?.attrs?.href);
		}

		if (Array.isArray(node.content)) {
			node.content.forEach(walk);
		}
	}

	walk(doc as AdfNode);

	return Array.from(countByUrl, ([url, count], occurrenceIndex) => ({
		url,
		occurrenceIndex,
		count,
	}));
}
